/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

type ScrubbableSentryBreadcrumb = {
  message?: string
  data?: Record<string, unknown>
  [key: string]: unknown
}

/** Loose Sentry event shape so browser and Node SDKs can share this helper without importing `@sentry/*`. */
export type ScrubbableSentryEvent = {
  user?: {
    id?: string | number
    email?: string
    ip_address?: string
    username?: string
    name?: string
    [key: string]: unknown
  } | null
  request?: {
    url?: string
    query_string?: string | Record<string, unknown> | [string, string][] | null
    cookies?: unknown
    data?: unknown
    headers?: Record<string, string | null | undefined> | [string, string | null | undefined][] | null
    [key: string]: unknown
  }
  exception?: {
    values?: {
      value?: string
      type?: string
      [key: string]: unknown
    }[]
  }
  breadcrumbs?:
    | ScrubbableSentryBreadcrumb[]
    | {
        values?: ScrubbableSentryBreadcrumb[]
      }
  extra?: Record<string, unknown>
  contexts?: Record<string, unknown>
  message?: string
  [key: string]: unknown
}

/** Loose Sentry log shape for `beforeSendLog`. */
export type ScrubbableSentryLog = {
  message?: string
  attributes?: Record<string, unknown>
  [key: string]: unknown
}

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
const EMAIL_PLACEHOLDER = '[email]'
const REDACTED = '[redacted]'

const SENSITIVE_HEADER_NAMES = new Set(['cookie', 'set-cookie', 'authorization', 'x-api-key', 'x-auth-token'])

/**
 * Replace email-like substrings in a string.
 */
export function redactEmailsInString(value: string): string {
  return value.replace(EMAIL_RE, EMAIL_PLACEHOLDER)
}

function redactEmailsInValue(value: unknown, depth = 0): unknown {
  if (depth > 8) {
    return value
  }
  if (typeof value === 'string') {
    return redactEmailsInString(value)
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactEmailsInValue(item, depth + 1))
  }
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      result[key] = redactEmailsInValue(nested, depth + 1)
    }
    return result
  }
  return value
}

/**
 * SDK hooks pass `breadcrumbs` as an array. The Sentry API serializes them as
 * `{ values }`. An array's `.values` method is truthy and not iterable, so it
 * must not be used as the crumb list.
 */
function sentryBreadcrumbs(event: ScrubbableSentryEvent): ScrubbableSentryBreadcrumb[] | undefined {
  const breadcrumbs = event.breadcrumbs
  if (Array.isArray(breadcrumbs)) {
    return breadcrumbs
  }
  if (breadcrumbs && Array.isArray(breadcrumbs.values)) {
    return breadcrumbs.values
  }
  return undefined
}

function isSensitiveHeaderName(name: string): boolean {
  return SENSITIVE_HEADER_NAMES.has(name.toLowerCase())
}

function scrubRequestHeaders(
  headers: NonNullable<ScrubbableSentryEvent['request']>['headers']
): NonNullable<ScrubbableSentryEvent['request']>['headers'] {
  if (!headers) {
    return headers
  }

  if (Array.isArray(headers)) {
    return headers.map(([name, value]): [string, string | null | undefined] =>
      isSensitiveHeaderName(name) ? [name, REDACTED] : [name, value]
    )
  }

  const scrubbed: Record<string, string | null | undefined> = {}
  for (const [name, value] of Object.entries(headers)) {
    scrubbed[name] = isSensitiveHeaderName(name) ? REDACTED : value
  }
  return scrubbed
}

function stripQueryFromUrl(url: string): string {
  const hashIndex = url.indexOf('#')
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : ''
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url
  const queryIndex = withoutHash.indexOf('?')
  if (queryIndex < 0) {
    return url
  }
  return `${withoutHash.slice(0, queryIndex)}${hash}`
}

/**
 * Scrub PII from a Sentry error or transaction event.
 * Primary: emails in strings, query strings.
 * Defense in depth: cookies, auth headers, request bodies, user.email / user.ip_address.
 */
export function scrubSentryEvent<T extends object>(event: T): T {
  const e = event as T & ScrubbableSentryEvent

  if (e.user && typeof e.user === 'object') {
    const { email: _email, ip_address: _ip, ...rest } = e.user
    e.user = rest
  }

  if (e.request && typeof e.request === 'object') {
    if (typeof e.request.url === 'string') {
      e.request.url = stripQueryFromUrl(e.request.url)
    }
    delete e.request.query_string
    delete e.request.cookies
    delete e.request.data
    if (e.request.headers) {
      e.request.headers = scrubRequestHeaders(e.request.headers)
    }
  }

  if (typeof e.message === 'string') {
    e.message = redactEmailsInString(e.message)
  }

  if (e.exception?.values) {
    for (const value of e.exception.values) {
      if (typeof value.value === 'string') {
        value.value = redactEmailsInString(value.value)
      }
    }
  }

  const breadcrumbs = sentryBreadcrumbs(e)
  if (breadcrumbs) {
    for (const crumb of breadcrumbs) {
      if (typeof crumb.message === 'string') {
        crumb.message = redactEmailsInString(crumb.message)
      }
      if (crumb.data) {
        crumb.data = redactEmailsInValue(crumb.data) as Record<string, unknown>
      }
    }
  }

  if (e.extra) {
    e.extra = redactEmailsInValue(e.extra) as Record<string, unknown>
  }

  if (e.contexts) {
    e.contexts = redactEmailsInValue(e.contexts) as Record<string, unknown>
  }

  return event
}

/**
 * Scrub PII from a Sentry log payload (`beforeSendLog`).
 */
export function scrubSentryLog<T extends object>(log: T): T {
  const l = log as T & ScrubbableSentryLog

  if (typeof l.message === 'string') {
    l.message = redactEmailsInString(l.message)
  }
  if (l.attributes) {
    l.attributes = redactEmailsInValue(l.attributes) as Record<string, unknown>
  }
  return log
}
